import React, { RefObject, useRef, memo } from 'react';
import TableHead from '../TableComponent/TableHead';
import TableBody from '../TableComponent/TableBody';
import styles from '../SplitScrollTable.module.scss';

interface MilestonesTableProps {
  data: any[];
  columnsConfig: any[];
  tableWidth?: number;
  showPhaseDatesOnly?: boolean;
  appendPhaseDatesTable?: boolean;
  phaseColumnsConfig?: any[];
  phaseTableWidth?: number;
  hoveredRow: string | null;
  expandedRows: Set<string>;
  rowHoveredClass: string;
  scrollContainerRef: RefObject<HTMLDivElement>;
  onScroll: () => void;
  onRowHover: (issueId: string) => void;
  onRowLeave: () => void;
  onToggleRow: (issueId: string) => void;
  // Shared chunking state
  renderedChunks?: number;
  setRenderedChunks?: (value: number | ((prev: number) => number)) => void;
  isLoadingMore?: boolean;
  setIsLoadingMore?: (value: boolean) => void;
}

export const MilestonesTable: React.FC<MilestonesTableProps> = memo(
  ({
    data,
    columnsConfig,
    tableWidth,
    showPhaseDatesOnly = false,
    appendPhaseDatesTable = false,
    phaseColumnsConfig = [],
    phaseTableWidth,
    hoveredRow,
    expandedRows,
    rowHoveredClass,
    scrollContainerRef,
    onScroll,
    onRowHover,
    onRowLeave,
    onToggleRow,
    renderedChunks,
    setRenderedChunks,
    isLoadingMore,
    setIsLoadingMore
  }) => {
    const headerScrollRef = useRef<HTMLDivElement>(null);

    const handleBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
      // Sync horizontal scroll to header
      if (headerScrollRef.current && scrollContainerRef.current) {
        headerScrollRef.current.scrollLeft =
          scrollContainerRef.current.scrollLeft;
      }
      onScroll();
    };

    const effectiveColumnsConfig = columnsConfig;

    return (
      <div
        className={`${styles.rightTableWrapper2} ${
          showPhaseDatesOnly ? styles.betaRightTableWrapper : ''
        }`}
      >
        {/* Sticky Header */}
        <div className={styles.rightTableHeaderWrapper} ref={headerScrollRef}>
          <div className={styles.rightTableRow}>
            <table
              className={`${styles.rightTable} ${
                showPhaseDatesOnly ? styles.betaTable : ''
              }`}
              style={
                tableWidth
                  ? {
                      width: tableWidth,
                      ['--beta-column-width' as any]: '100px'
                    }
                  : undefined
              }
            >
              <TableHead columns={effectiveColumnsConfig} />
            </table>

            {appendPhaseDatesTable && phaseColumnsConfig.length > 0 && (
              <table
                className={`${styles.rightTable} ${styles.betaTable}`}
                style={
                  phaseTableWidth
                    ? {
                        width: phaseTableWidth,
                        ['--beta-column-width' as any]: '100px'
                      }
                    : undefined
                }
              >
                <TableHead columns={phaseColumnsConfig} />
              </table>
            )}
          </div>
        </div>

        {/* Scrollable Body */}
        <div
          className={styles.rightTableBodyWrapper}
          ref={scrollContainerRef}
          onScroll={handleBodyScroll}
        >
          <div className={styles.rightTableRow}>
            <table
              className={`${styles.rightTable} ${
                showPhaseDatesOnly ? styles.betaTable : ''
              }`}
              style={
                tableWidth
                  ? {
                      width: tableWidth,
                      ['--beta-column-width' as any]: '100px'
                    }
                  : undefined
              }
            >
              <TableBody
                data={data}
                columnsConfig={effectiveColumnsConfig}
                type="milestones"
                hoveredRow={hoveredRow}
                expandedRows={expandedRows}
                rowHoveredClass={rowHoveredClass}
                onRowHover={onRowHover}
                onRowLeave={onRowLeave}
                onToggleRow={onToggleRow}
                renderedChunks={renderedChunks}
                setRenderedChunks={setRenderedChunks}
                isLoadingMore={isLoadingMore}
                setIsLoadingMore={setIsLoadingMore}
              />
            </table>

            {appendPhaseDatesTable && phaseColumnsConfig.length > 0 && (
              <table
                className={`${styles.rightTable} ${styles.betaTable}`}
                style={
                  phaseTableWidth
                    ? {
                        width: phaseTableWidth,
                        ['--beta-column-width' as any]: '100px'
                      }
                    : undefined
                }
              >
                <TableBody
                  data={data}
                  columnsConfig={phaseColumnsConfig}
                  type="milestones"
                  hoveredRow={hoveredRow}
                  expandedRows={expandedRows}
                  rowHoveredClass={rowHoveredClass}
                  onRowHover={onRowHover}
                  onRowLeave={onRowLeave}
                  onToggleRow={onToggleRow}
                  renderedChunks={renderedChunks}
                  setRenderedChunks={setRenderedChunks}
                  isLoadingMore={isLoadingMore}
                  setIsLoadingMore={setIsLoadingMore}
                />
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }
);

MilestonesTable.displayName = 'MilestonesTable';
